import { UnidadePrazo } from './canal-publico.model';

/**
 * Catálogo fechado de cinco tipos do módulo de Ouvidoria. Acesso à informação **não** entra
 * aqui: o pedido do SIC corre no e-SIC, com entidade e prazo próprios, e a API rejeita o tipo.
 */
export type TipoManifestacaoOuvidoria =
  'Reclamacao' | 'Elogio' | 'Sugestao' | 'Solicitacao' | 'Denuncia';

/**
 * Rota do portal → chave do catálogo da API. As rotas nasceram no plural ("elogios",
 * "sugestoes") e o back-end usa o singular; o mapa existe para a tradução acontecer num lugar
 * só. Rota fora do mapa não envia — a API rejeitaria o tipo.
 */
export const TIPO_POR_ROTA: Readonly<Record<string, TipoManifestacaoOuvidoria>> = {
  reclamacao: 'Reclamacao',
  elogios: 'Elogio',
  sugestoes: 'Sugestao',
  solicitacao: 'Solicitacao',
  denuncia: 'Denuncia',
};

/** Como o cidadão quer receber a resposta. Chaves do enum do back-end. */
export type FormaResposta = 'Email' | 'Sistema' | 'Presencial' | 'Telefone' | 'Correio';

export interface OpcaoFormaResposta {
  chave: FormaResposta;
  rotulo: string;
}

export const FORMAS_RESPOSTA: readonly OpcaoFormaResposta[] = [
  { chave: 'Email', rotulo: 'Por e-mail' },
  { chave: 'Sistema', rotulo: 'Pelo portal, com o número do protocolo' },
  { chave: 'Telefone', rotulo: 'Por telefone' },
  { chave: 'Correio', rotulo: 'Por carta, no endereço informado' },
  { chave: 'Presencial', rotulo: 'Presencialmente, na Ouvidoria' },
];

/** As 27 unidades federativas — a cidade é texto livre, o back-end não valida catálogo. */
export const UFS: readonly string[] = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

/**
 * O payload da abertura pública (`POST api/ouvidoria/publico/manifestacao`, multipart).
 *
 * ⚠️ Nenhum campo do SISTEMA existe aqui — nem `codOrgaoOrg` (resolvido pelo host do portal),
 * nem `protocolo`, `situacao`, `dataEnvio`, `ip`, `canalOrigem`, `motivoRecusa`,
 * `codOuvidoriaOvi` ou `codigoAcesso`. Todos são propriedades-isca no back-end: mandá-los
 * devolve 400 nomeando o campo, e não um valor ignorado em silêncio.
 */
export interface AberturaManifestacao {
  desTipoManifestacao: TipoManifestacaoOuvidoria;
  desAssunto: string;
  desDescricao: string;

  bitAnonima: boolean;
  bitSigiloIdentidade: boolean;
  bitDesejaResposta: boolean;
  bitAceiteComunicacao: boolean;
  desFormaResposta: FormaResposta;

  /** Ausentes na manifestação anônima — a fábrica anônima do back-end nem os recebe. */
  desNome?: string;
  desCpfCnpj?: string;
  desEmail?: string;
  desTelefone?: string;

  desEndereco?: string;
  desBairro?: string;
  desCidade?: string;
  desUf?: string;
}

/** O corpo do 201 da abertura. É daqui que a etapa de Confirmação tira tudo o que mostra. */
export interface ManifestacaoAbertura {
  codManifestacaoMan: number;

  /** `OUV-{ano}-{6 dígitos}-{5 base32}`. A credencial da consulta — não há segundo segredo. */
  protocolo: string;

  /** `Recebida` na abertura normal; `Recusada` na recusa automática. */
  situacao: string;

  tipo: string;
  assunto: string;
  dataAbertura: string;

  prazoResposta: number;
  unidadePrazo: UnidadePrazo;
  dataLimiteResposta: string;

  /** A ouvidoria que EFETIVAMENTE recebeu. */
  ouvidoria?: string | null;

  /** Preenchida quando a de destino natural estava travada e houve redirecionamento. */
  ouvidoriaOrigem?: string | null;

  redirecionada: boolean;

  /** Recebida, mas parada até haver responsável apto. O prazo corre do mesmo jeito. */
  retida: boolean;

  /** Aviso pt-BR de retenção ou de recusa automática. Nulo no caminho feliz. */
  aviso?: string | null;

  /** `false` na anônima (não há destinatário) e quando a ouvidoria tem o toggle desligado. */
  notificacaoEnfileirada: boolean;
}

/**
 * O que a consulta por protocolo devolve. Contrato restrito por construção: nenhum dado do
 * solicitante, porque quem tem o protocolo não é necessariamente quem abriu.
 */
export interface ManifestacaoPublica {
  protocolo: string;
  tipo: string;
  assunto: string;
  situacao: string;
  dataAbertura: string;

  prazoResposta: number;
  unidadePrazo: UnidadePrazo;
  dataLimiteResposta: string;

  ouvidoria?: string | null;

  anonima: boolean;
  retida: boolean;
  redirecionada: boolean;

  /** Só os NOMES dos anexos da abertura — são do próprio cidadão, e ele já os tem. */
  anexos: readonly string[];

  aceitaComplemento: boolean;
  aceitaContestacao: boolean;
  dataLimiteContestacao?: string | null;
  justificativaObrigatoria: boolean;

  timeline: readonly EventoManifestacaoPublico[];
}

/** Um evento visível ao cidadão. Não existe observação interna neste contrato. */
export interface EventoManifestacaoPublico {
  tipo: string;
  autor?: string | null;
  /** `Sistema` | `Cidadao` | `Ouvidoria` | `Departamento`. */
  origem: string;
  mensagem?: string | null;
  situacaoNova: string;
  dataHora: string;
  anexos: readonly AnexoEventoPublico[];
}

export interface AnexoEventoPublico {
  id: number;
  nome: string;
  url: string;
  contentType?: string | null;
  tamanhoBytes: number;
  dataEnvio: string;
}

/**
 * Erro da superfície pública já traduzido para o que a tela precisa decidir: para onde levar o
 * cidadão e o que escrever. A `mensagem` vem da API sempre que ela mandou uma — regra de
 * negócio da ouvidoria é dela, não do portal.
 */
export interface ErroOuvidoriaPublica {
  tipo: 'validacao' | 'limite' | 'sem-canal' | 'nao-encontrado' | 'indisponivel' | 'desafio';
  mensagem: string;
  detalhes: readonly string[];
}
